import React, { Reducer, createContext, useContext } from 'react';

import {
  ChooseEmojiScreen,
  GoToMarketplaceScreen,
  ThankYouScreen,
  WriteFeedbackScreen,
} from './screens';

export type ReducerState =
  | {
      screen: {
        name: 'popup-closed';
        content: null;
      };
    }
  | {
      screen: {
        name: 'choose-emoji-screen';
        content: JSX.Element;
      };
    }
  | {
      screen: {
        name: 'write-feedback-screen';
        content: JSX.Element;
      };
    }
  | {
      screen: {
        name: 'go-to-marketplace-screen';
        content: JSX.Element;
      };
    }
  | {
      screen: {
        name: 'thank-you-screen';
        content: JSX.Element;
      };
    };

export type ReducerAction =
  | { type: 'open-popup' }
  | { type: 'close-popup' }
  | { type: 'toggle-popup' }
  | {
      type: 'click-bad-emoji';
    }
  | {
      type: 'click-good-emoji';
    }
  | {
      type: 'click-skip-feedback';
    }
  | { type: 'click-send-feedback' }
  | {
      type: 'click-ok';
    }
  | {
      type: 'click-write-a-review-on-marketplace';
    };

export const reducer: Reducer<ReducerState, ReducerAction> = (
  state,
  action
) => {
  try {
    switch (action.type) {
      case 'toggle-popup': {
        if (state.screen.name === 'popup-closed') {
          return reducer(state, { type: 'open-popup' });
        }
        return reducer(state, { type: 'close-popup' });
      }
      case 'open-popup': {
        return {
          ...state,
          screen: {
            name: 'choose-emoji-screen',
            content: <ChooseEmojiScreen />,
          },
        };
      }
      case 'close-popup': {
        return {
          ...state,
          screen: {
            name: 'popup-closed',
            content: null,
          },
        };
      }
      case 'click-bad-emoji': {
        return {
          ...state,
          screen: {
            name: 'write-feedback-screen',
            content: <WriteFeedbackScreen />,
          },
        };
      }
      case 'click-good-emoji': {
        return {
          ...state,
          screen: {
            name: 'go-to-marketplace-screen',
            content: <GoToMarketplaceScreen />,
          },
        };
      }
      case 'click-skip-feedback': {
        return {
          ...state,
          screen: {
            name: 'thank-you-screen',
            content: <ThankYouScreen />,
          },
        };
      }
      case 'click-send-feedback': {
        return {
          ...state,
          screen: {
            name: 'thank-you-screen',
            content: <ThankYouScreen />,
          },
        };
      }
      case 'click-write-a-review-on-marketplace': {
        return {
          ...state,
          screen: {
            name: 'thank-you-screen',
            content: <ThankYouScreen />,
          },
        };
      }
      case 'click-ok': {
        return {
          ...state,
          screen: {
            name: 'popup-closed',
            content: null,
          },
        };
      }
      default:
        throw new Error('Invalid action');
    }
  } catch {
    return reducer(state, { type: 'close-popup' });
  }
};

export const initialState: ReducerState = {
  screen: { name: 'popup-closed', content: null },
};

export const FeedbackStateContext =
  createContext<React.ReducerState<typeof reducer>>(null);

export const FeedbackDispatchContext =
  createContext<React.Dispatch<React.ReducerAction<typeof reducer>>>(null);

export const useFeedbackState = (): ReducerState => {
  return useContext(FeedbackStateContext);
};

export const useFeedbackDispatch = (): React.Dispatch<ReducerAction> => {
  return useContext(FeedbackDispatchContext);
};

type Props = { children: React.ReactNode };

export default function FeedbackReducerProvider({
  children,
}: Props): JSX.Element {
  const [state, dispatch] = React.useReducer(reducer, initialState);

  return (
    <FeedbackStateContext.Provider value={state}>
      <FeedbackDispatchContext.Provider value={dispatch}>
        {children}
      </FeedbackDispatchContext.Provider>
    </FeedbackStateContext.Provider>
  );
}
